import React, { useEffect, useState, useMemo } from "react";
import style from "./style.module.scss";
import logo from "../../../assets/images/logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../modules/store/hooks";
import { modalsSlice } from "../../../modules/store/reducers/modalsSlice";
import { IconButton, Tooltip } from "@mui/material";
import ActiveDevicesModal from "../../Modals/ActiveDevicesModal";
import SendOtpModal from "../../Modals/SendOtpModal";
import DevicesIcon from "@mui/icons-material/Devices";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CampaignIcon from "@mui/icons-material/Campaign";
import EventIcon from "@mui/icons-material/Event";
import LogoutIcon from "@mui/icons-material/Logout";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CancelIcon from "@mui/icons-material/Cancel";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import KeyIcon from "@mui/icons-material/Key";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PolicyIcon from "@mui/icons-material/Policy";
import { statisticApi } from "../../../modules/services/statisticService";
import {
  logOut,
  setLanguage,
  toggleLanguage,
} from "../../../modules/store/reducers/authSlice";
import { LOCALES } from "../../../i18n";
import translate from "../../../i18n/translate";
import { deviceApi } from "../../../modules/services/deviceService";
import DownloadReportModal from "../../Modals/DownloadReportModal";
import CancelTicketModal from "../../Modals/CancelTicketModal";
import CancelTicketByUuidModal from "../../Modals/CancelTicketByUuidModal";
import ChangeOwnerByLinkModal from "../../Modals/ChangeOwnerByLinkModal";
import EventTypesModal from "../../Modals/EventTypesModal";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CategoryIcon from "@mui/icons-material/Category";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SmsIcon from "@mui/icons-material/Sms";
import SendPushNotificationModal from "../../Modals/SendPushNotificationModal";
import SendSmsBroadcastModal from "../../Modals/SendSmsBroadcastModal";

type NavEntry = {
  label: React.ReactNode;
  path: string;
  matcher: (pathname: string) => boolean;
  icon: React.ReactNode;
};

function formatMoney(value: number | string | undefined): string {
  if (value === undefined || value === null || value === "") return "0";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    toggleActiveDevicesModal,
    toggleSendOtpModal,
    toggleDownloadReportModal,
    toggleCancelTicketModal,
    toggleCancelTicketByUuidModal,
    toggleChangeOwnerByLinkModal,
    toggleEventTypesModal,
    toggleSendPushNotificationModal,
    toggleSendSmsBroadcastModal,
  } = modalsSlice.actions;

  const changeOwnerByLinkOpen = useAppSelector((state) => state.modalsReducer.changeOwnerByLink) === "open";
  const eventTypesOpen = useAppSelector((state) => state.modalsReducer.eventTypes) === "open";
  const sendPushOpen = useAppSelector((state) => state.modalsReducer.sendPushNotification) === "open";
  const sendSmsOpen = useAppSelector((state) => state.modalsReducer.sendSmsBroadcast) === "open";

  const [disconnectDevice] = deviceApi.useDeleteDeviceMutation();
  const [countOfConnectedDevices, setCountOfConnectedDevices] = useState<number>(0);
  const [statistic, setStatistic] = useState<any>({});
  const { language, sessionID, token } = useAppSelector((state) => state.auth);

  const onLanguageToggle = (locale: string) => {
    window.localStorage.setItem("locale", locale);
    dispatch(toggleLanguage());
  };

  const { data: statisticData } = statisticApi.useFetchStatisticQuery("", { skip: !sessionID });

  const logout = () => {
    disconnectDevice(sessionID)
      .then(() => {
        dispatch(logOut());
        navigate("/login");
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    if (statisticData) setStatistic(statisticData);
  }, [statisticData]);

  useEffect(() => {
    const locale = window.localStorage.getItem("locale");
    if (locale) dispatch(setLanguage(locale));
  }, [dispatch]);

  const navEntries: NavEntry[] = useMemo(
    () => [
      {
        label: translate("headerOpenEvents"),
        path: "/events/all",
        matcher: (p) => p === "/" || p.startsWith("/events"),
        icon: <EventIcon />,
      },
      {
        label: translate("headerOpenProfiles"),
        path: "/profiles",
        matcher: (p) => p.startsWith("/profiles"),
        icon: <PersonIcon />,
      },
      {
        label: translate("headerOpenOrganizers"),
        path: "/organizers/all",
        matcher: (p) => p.startsWith("/organizers") && !p.startsWith("/organizers/request"),
        icon: <GroupsIcon />,
      },
      {
        label: translate("headerOpenOrganizersRequest"),
        path: "/organizers/request/list",
        matcher: (p) => p.startsWith("/organizers/request"),
        icon: <PendingActionsIcon />,
      },
      {
        label: translate("headerOpenTransactions"),
        path: "/transactions/all",
        matcher: (p) => p.startsWith("/transactions"),
        icon: <ReceiptLongIcon />,
      },
      {
        label: translate("headerOpenMarketingPersons"),
        path: "/marketing-persons/all",
        matcher: (p) => p.startsWith("/marketing-persons"),
        icon: <CampaignIcon />,
      },
      {
        label: translate("headerOpenStaticOtp"),
        path: "/static-otp",
        matcher: (p) => p.startsWith("/static-otp"),
        icon: <KeyIcon />,
      },
      {
        label: translate("headerOpenOtpBypass"),
        path: "/captcha-bypass",
        matcher: (p) => p.startsWith("/captcha-bypass"),
        icon: <VerifiedUserIcon />,
      },
      {
        label: translate("headerOpenRobotSmsPolicy"),
        path: "/robot-sms-policy",
        matcher: (p) => p.startsWith("/robot-sms-policy"),
        icon: <PolicyIcon />,
      },
    ],
    [],
  );

  const statCards = useMemo(
    () => [
      { label: translate("headerTotalMoney"), value: statistic.total },
      { label: translate("headerTotalFee"), value: statistic.totalFee },
      { label: translate("headerTransactionTotalFee"), value: statistic.transactionTotalFee },
      { label: translate("headerTransactionTotalFastFee"), value: statistic.transactionTotalFastFee },
      { label: translate("headerTransactionTotalEarlyFee"), value: statistic.transactionTotalEarlyFee },
      { label: translate("headerTransactionTotalAdminWithdrawalFee"), value: statistic.transactionTotalAdminWithdrawalFee },
      { label: 'Marketing payouts (total)', value: (statistic as any).marketingPayoutsTotal },
      { label: 'Marketing — event commission', value: (statistic as any).marketingPayoutsEventCommission },
      { label: 'Marketing — referral commission', value: (statistic as any).marketingPayoutsReferralCommission },
    ],
    [statistic],
  );

  if (!token || location.pathname === "/login") return null;

  return (
    <>
      <header>
        <div className={style.Wrapper}>
          <div className={style.TopRow}>
            <div className={style.Brand}>
              <img alt="logo" src={logo} className={style.BrandLogo} />
              <div className={style.BrandBlock}>
                <div className={style.BrandTitleRow}>
                  <span className={style.BrandTitle}>SELECTOR</span>
                </div>
                <div className={style.BrandChips}>
                  <button
                    type="button"
                    className={style.Chip}
                    onClick={() => dispatch(toggleActiveDevicesModal())}
                    title={String(translate("headerDevicesConnected"))}
                  >
                    <DevicesIcon />
                    {countOfConnectedDevices}
                  </button>
                  <button
                    type="button"
                    className={style.Chip}
                    onClick={() => dispatch(toggleSendOtpModal())}
                    title={String(translate("headerOptToUsers"))}
                  >
                    <SmsOutlinedIcon />
                    OTP
                  </button>
                </div>
              </div>
            </div>

            <nav className={style.Nav}>
              {navEntries.map((entry) => {
                const isActive = entry.matcher(location.pathname);
                return (
                  <span
                    key={entry.path}
                    onClick={() => navigate(entry.path)}
                    className={`${style.NavItem} ${isActive ? style.NavActive : ""}`}
                  >
                    {entry.icon}
                    {entry.label}
                  </span>
                );
              })}
            </nav>

            <div className={style.Actions}>
              <div className={style.LangToggle}>
                <button
                  type="button"
                  onClick={() => onLanguageToggle(LOCALES.ENGLISH)}
                  className={`${style.LangBtn} ${language === LOCALES.ENGLISH ? style.LangActive : ""}`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => onLanguageToggle(LOCALES.HEBREW)}
                  className={`${style.LangBtn} ${language === LOCALES.HEBREW ? style.LangActive : ""}`}
                >
                  HE
                </button>
              </div>

              <Tooltip title={translate("headerManageEventTypes")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleEventTypesModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("headerManageEventTypes"))}
                >
                  <CategoryIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("sendPushNotificationTitle")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleSendPushNotificationModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("sendPushNotificationTitle"))}
                >
                  <NotificationsActiveIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("sendSmsBroadcastTitle")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleSendSmsBroadcastModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("sendSmsBroadcastTitle"))}
                >
                  <SmsIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("eventsScreenChangeOwnerByLink")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleChangeOwnerByLinkModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("eventsScreenChangeOwnerByLink"))}
                >
                  <SwapHorizIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("headerDownloadReport")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleDownloadReportModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("headerDownloadReport"))}
                >
                  <AssessmentIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("headerCancelTicket")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleCancelTicketModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("headerCancelTicket"))}
                >
                  <CancelIcon />
                </button>
              </Tooltip>
              <Tooltip title={translate("headerCancelTicketByUuid")}>
                <button
                  type="button"
                  onClick={() => dispatch(toggleCancelTicketByUuidModal())}
                  className={style.ActionBtnGhost}
                  aria-label={String(translate("headerCancelTicketByUuid"))}
                >
                  <FingerprintIcon />
                </button>
              </Tooltip>

              <Tooltip title="Logout">
                <IconButton onClick={logout} className={style.IconBtn} size="small">
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className={style.StatsRow}>
            {statCards.map((card, i) => (
              <div key={i} className={style.StatCard}>
                <span className={style.StatLabel}>{card.label}</span>
                <span className={style.StatValue}>
                  {formatMoney(card.value)}
                  <span className={style.StatCurrency}>₪</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <ActiveDevicesModal
        count={countOfConnectedDevices}
        setCount={setCountOfConnectedDevices}
      />
      <DownloadReportModal />
      <CancelTicketModal />
      <CancelTicketByUuidModal />
      <SendOtpModal />
      {changeOwnerByLinkOpen && <ChangeOwnerByLinkModal />}
      {eventTypesOpen && <EventTypesModal />}
      {sendPushOpen && <SendPushNotificationModal />}
      {sendSmsOpen && <SendSmsBroadcastModal />}
    </>
  );
};

export default Header;
